import React from "react";
import PolicyLayout from "../PolicyLayout";
import { linkifyNBFC } from "../../utils/nbfcLink";

const KYCAmlPolicy: React.FC = () => {
  const policySections = [
    {
      title: `KYC & AML Policy for ${import.meta.env.VITE_COMPANY_NAME}`,
      content: `${import.meta.env.VITE_COMPANY_NBFC} is an RBI-registered NBFC specializing in short-term personal loans.`,
    },
    {
      title: "1. Objective",
      content:
        "To prevent misuse of the NBFC’s services for money laundering, terrorist financing, and other unlawful activities by implementing robust Know Your Customer (KYC) and Anti-Money Laundering (AML) procedures.",
    },
    {
      title: "2. Regulatory Framework",
      list: [
        "RBI Master Direction – KYC, 2016 (updated as on August 14, 2025).",
        "Prevention of Money Laundering Act (PMLA), 2002.",
        "RBI’s AML/CFT guidelines and all subsequent circulars and amendments.",
      ],
    },
    {
      title: "3. Customer Acceptance Policy (CAP)",
      list: [
        "No account shall be opened anonymously or under fictitious/benami names.",
        "Mandatory collection of valid identity and address proof before loan disbursal.",
        "Enhanced due diligence for high-risk customers such as PEPs or NRIs.",
        "Accounts shall not be opened if the customer refuses to provide required documentation.",
      ],
    },
    {
      title: "4. Customer Identification Procedure (CIP)",
      list: [
        "Verification through valid KYC documents such as Aadhaar, PAN, Passport, or Voter ID.",
        "Biometric authentication via UIDAI where applicable.",
        "Video KYC permitted for onboarding, subject to regulatory compliance.",
        "Periodic re-KYC for existing customers based on their risk categorization.",
      ],
    },
    {
      title: "5. Risk Categorization",
      list: [
        "Low Risk: Salaried individuals and government employees.",
        "Medium Risk: Self-employed and small business owners.",
        "High Risk: Politically Exposed Persons (PEPs), cash-intensive businesses.",
        "Risk-based KYC updating frequency will apply accordingly.",
      ],
    },
    {
      title: "6. Monitoring of Transactions",
      list: [
        "Automated alert systems will identify suspicious activities such as frequent cash deposits or round-tripping.",
        "Threshold-based reports will be generated for FIU-IND.",
        "Real-time flagging of high-value or unusual transactions.",
      ],
    },
    {
      title: "7. Record Keeping",
      list: [
        "Maintain KYC records for at least 5 years post account closure.",
        "Transaction records retained for 5 years from the transaction date.",
        "All data will be securely stored and made available for regulatory inspection when required.",
      ],
    },
    {
      title: "8. Reporting to Authorities",
      list: [
        "Suspicious Transaction Reports (STRs) will be filed with FIU-IND within prescribed timelines.",
        "Cash Transaction Reports (CTRs) will be filed for cash transactions over ₹10 lakh.",
        "Compliance with RBI’s AML/CFT periodic reporting formats will be ensured.",
      ],
    },
    {
      title: "9. Employee Training & Accountability",
      list: [
        "Ongoing employee training on KYC/AML procedures, red flags, and escalation processes.",
        "Standard operating procedures for onboarding, documentation, and verification.",
        "Defined accountability and escalation matrix for credit and operations teams.",
      ],
    },
    {
      title: "10. Internal Audit & Review",
      list: [
        "Quarterly internal audits focused on KYC/AML compliance effectiveness.",
        "Annual policy review by senior management and the Board.",
        "Timely corrective measures for any lapses or identified deficiencies.",
      ],
    },
  ];

  return (
    <PolicyLayout 
      title="KYC & AML Policy" 
      subtitle={`At ${import.meta.env.VITE_COMPANY_NAME}, we maintain the highest standards of financial integrity. Our KYC and AML procedures are built on trust and absolute transparency.`}
      badge="Compliance Vault 🛡️"
    >
      {policySections.map((section, idx) => (
        <div key={idx} className="space-y-6">
          <h2 className="text-3xl font-black text-[#0F172A] tracking-tight">
            {linkifyNBFC(section.title)}
          </h2>

          {section.content && (
            <p className="text-[var(--text-secondary)] text-lg leading-relaxed font-medium">
              {linkifyNBFC(section.content)}
            </p>
          )}
          
          {section.list && (
            <ul className="space-y-4">
              {section.list.map((li, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--text-secondary)] text-lg font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] mt-2.5 flex-shrink-0" />
                  {linkifyNBFC(li)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </PolicyLayout>
  );
};

export default KYCAmlPolicy;
